import React, { FC, useContext, Fragment } from 'react'
import styled from 'styled-components'
import KeyHandler, { KEYUP } from 'react-key-handler'

import { rem } from 'polished'

import { Flex } from 'components/Box'
import { IconButton } from 'components/Button'

import ModalContext from 'containers/ModalManager/ModalContext'

import { ReactComponent as ChevronRight } from 'icons/circle.chevron.right.svg'

const ChevronLeft = styled(ChevronRight)`
  transform: rotate(180deg);
`

const PaginationControlsNumbers = styled.span`
  font-family: ${({ theme }) => theme.fonts.buttons};
  font-size: ${({ theme }) => theme.fontSizes.smaller};
  color: ${({ theme }) => theme.colors.text.dark.base};
  margin-top: -1px; /* HACK: vertical align with buttons */ /* stylelint-disable-line scales/space */
  font-variant-numeric: tabular-nums;
`

const PaginationControlsButton = styled(IconButton).attrs({
  size: 20,
})`
  color: ${({ theme }) => theme.colors.text.dark.secondary};
  margin-left: ${rem(9)}; /* stylelint-disable-line scales/space */
  margin-right: ${rem(9)}; /* stylelint-disable-line scales/space */

  &:hover {
    color: ${({ theme }) => theme.colors.text.dark.base};
  }

  :disabled {
    opacity: 0.5;
  }
`

type PaginationControlsProps = {
  total: number
  current: number
  onClickNext: () => void
  onClickPrevious: () => void
  showNumbers: boolean
}

const PaginationControls: FC<PaginationControlsProps> = ({
  total,
  current,
  onClickNext,
  onClickPrevious,
  showNumbers,
  ...props
}) => {
  const { isModalOpen } = useContext(ModalContext)

  const disablePrevious = current <= 1 || !onClickPrevious || isModalOpen
  const disableNext = current === total || !onClickNext || isModalOpen

  return (
    <Fragment>
      {!disablePrevious && (
        <KeyHandler
          keyEventName={KEYUP}
          keyCode={37}
          keyValue="ArrowLeft"
          onKeyHandle={onClickPrevious}
        />
      )}

      {!disableNext && (
        <KeyHandler
          keyEventName={KEYUP}
          keyCode={39}
          keyValue="ArrowRight"
          onKeyHandle={onClickNext}
        />
      )}
      <Flex alignItems="center" {...props}>
        <PaginationControlsButton
          data-testid="pagination-previous"
          icon={ChevronLeft}
          onClick={onClickPrevious}
          disabled={disablePrevious}
        />
        {showNumbers && (
          <PaginationControlsNumbers>
            {current}/{total}
          </PaginationControlsNumbers>
        )}
        <PaginationControlsButton
          data-testid="pagination-next"
          icon={ChevronRight}
          onClick={onClickNext}
          disabled={disableNext}
        />
      </Flex>
    </Fragment>
  )
}

PaginationControls.defaultProps = {
  showNumbers: true,
}

export default PaginationControls
